import { fromZonedTime, toZonedTime, format as tzFormat } from "date-fns-tz";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Game, GameWithSubscription } from "./types";
import type { DateFilter } from "@/v2/components/filters/types";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const ET_TIMEZONE = "America/New_York";

export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || ET_TIMEZONE;
}

export function convertDateRangeToET(
  dateFilter: DateFilter
): { start: string; end: string } {
  const userTz = getUserTimezone();

  if (!dateFilter.start_date && !dateFilter.end_date) {
    return { start: "", end: "" };
  }

  const start = dateFilter.start_date
    ? tzFormat(
        fromZonedTime(`${dateFilter.start_date} 00:00:00`, userTz),
        "yyyy-MM-dd HH:mm:ss",
        { timeZone: ET_TIMEZONE }
      )
    : "";

  const end = dateFilter.end_date
    ? tzFormat(
        fromZonedTime(`${dateFilter.end_date} 23:59:59`, userTz),
        "yyyy-MM-dd HH:mm:ss",
        { timeZone: ET_TIMEZONE }
      )
    : "";

  return { start, end };
}

/**
 * Parses a game's raw date-time string (ET-implicit if no offset is
 * present, otherwise absolute) into a dayjs instant. Exported so callers
 * that need the same instant `formatGameDate`/`formatGameTime` use - e.g.
 * to derive a "Today"/"Tomorrow" label in the same zone the time is
 * rendered in - don't have to re-implement the format-guessing.
 */
export function parseGameDateTime(gameDateTime: string) {
  const normalized = gameDateTime.trim().toUpperCase();
  const hasMeridiem = /AM|PM/.test(normalized);
  
  let formats: string[];
  if (hasMeridiem) {
    formats = [
      "YYYY-MM-DD hh:mm:ss.SSSSSS A Z",
      "YYYY-MM-DD hh:mm:ss.SSSSSS A",
      "YYYY-MM-DD hh:mm:ss A Z",
      "YYYY-MM-DD hh:mm:ss A",
      "YYYY-MM-DD hh:mm A Z",
      "YYYY-MM-DD hh:mm A",
    ];
  } else {
    formats = [
      "YYYY-MM-DD HH:mm:ss.SSSSSS Z",
      "YYYY-MM-DD HH:mm:ss.SSSSSS",
      "YYYY-MM-DD HH:mm:ss Z",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm Z",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD",
    ];
  }
  
  for (const fmt of formats) {
    try {
      const parsedUtc = dayjs.utc(normalized, fmt);
      if (parsedUtc.isValid()) {
        const hasOffset = fmt.endsWith("Z");
        if (hasOffset) {
          return parsedUtc;
        } else {
          return dayjs.tz(normalized, fmt, ET_TIMEZONE);
        }
      }
    } catch {
      // safe fallback
    }
  }
  
  const nativeDate = new Date(gameDateTime);
  if (!isNaN(nativeDate.getTime())) {
    return dayjs(nativeDate);
  }
  
  return dayjs(NaN);
}

export function formatGameDate(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return "";
    const utcDate = new Date(parsed.valueOf());
    const userDate = toZonedTime(utcDate, userTz);
    return tzFormat(userDate, "MMM dd yyyy", { timeZone: userTz }).toUpperCase();
  } catch {
    return "";
  }
}

/**
 * Weekday and day with no year, e.g. "Sat, Sep 13".
 *
 * For surfaces that already state the year elsewhere or do not need it at all
 * (the v2 countdown screen prints this above the face-off time). Title case, so
 * a caller that wants it shouted can apply `uppercase` itself; going the other
 * way is not possible.
 */
export function formatGameWeekdayDate(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return "";
    const utcDate = new Date(parsed.valueOf());
    const userDate = toZonedTime(utcDate, userTz);
    return tzFormat(userDate, "EEE, MMM d", { timeZone: userTz });
  } catch {
    return "";
  }
}

/**
 * Long date for the schedule's date section headers, e.g.
 * "MONDAY, SEPTEMBER 04, 2026". Distinct string per calendar day, so it also
 * doubles as the change-detection key between consecutive games.
 */
export function formatGameDateLong(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return "";
    const utcDate = new Date(parsed.valueOf());
    const userDate = toZonedTime(utcDate, userTz);
    return tzFormat(userDate, "EEEE, MMMM d, yyyy", {
      timeZone: userTz,
    }).toUpperCase();
  } catch {
    return "";
  }
}

function tzNamePart(date: Date, timeZone: string, style: "short" | "long"): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: style,
  }).formatToParts(date);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
}

/**
 * Civil timezone abbreviation (IST, EDT, CDT, BST, ...), matching what the v1
 * page produced via moment's `z` token. Intl's short name is already an
 * abbreviation for US zones (EDT/CDT/PDT) but a GMT offset for others
 * (Asia/Kolkata -> "GMT+5:30"), so for those we take the initials of the long
 * name ("India Standard Time" -> "IST", "British Summer Time" -> "BST").
 */
function getTimezoneAbbreviation(date: Date, timeZone: string): string {
  try {
    const short = tzNamePart(date, timeZone, "short");
    if (short && !short.startsWith("GMT")) return short;

    const long = tzNamePart(date, timeZone, "long");
    if (long && !long.startsWith("GMT")) {
      const abbr = long
        .split(/\s+/)
        .filter((word) => /^[A-Z]/.test(word))
        .map((word) => word[0])
        .join("");
      if (abbr.length >= 2) return abbr;
    }
    return short;
  } catch {
    return "";
  }
}

export function formatGameTime(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return "";
    const utcDate = new Date(parsed.valueOf());
    const userDate = toZonedTime(utcDate, userTz);
    const timeStr = tzFormat(userDate, "h:mm a", { timeZone: userTz }).toUpperCase();
    const tzAbbr = getTimezoneAbbreviation(utcDate, userTz);
    return tzAbbr ? `${timeStr} ${tzAbbr}` : timeStr;
  } catch {
    return "";
  }
}

export function formatGameDateForLog(gameDateTime?: string): string {
  if (!gameDateTime) return "";
  try {
    const parsed = parseGameDateTime(gameDateTime);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD @ hh:mm A") : "";
  } catch {
    return "";
  }
}

export function isCurrentTimeMoreThanTenMinutesBefore(gameDateTime?: string): boolean {
  if (!gameDateTime) return false;
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return false;
    const gameTimeMinus10 = parsed.tz(userTz).subtract(10, "minute");
    const now = dayjs().tz(userTz);
    return now.isAfter(gameTimeMinus10);
  } catch {
    return false;
  }
}

/**
 * Whether a game belongs in an "Upcoming" list: not canceled (game_status
 * 90 "Broadcast Canceled" / 95 "Game Canceled") and not already started -
 * mirrors the Events page's Live tab cutoff, where a game counts as "live"
 * (not "upcoming") once it's within 10 minutes of its start time.
 *
 * `includeCanceled` keeps canceled games that are still before their start
 * time: the schedule page's Upcoming tab shows them with a Canceled chip
 * (HTPTV2-87), while the home/lens rails keep the default and drop them.
 */
export function isUpcomingGame(
  game: Game | GameWithSubscription,
  { includeCanceled = false }: { includeCanceled?: boolean } = {},
): boolean {
  if (
    !includeCanceled &&
    (game.game_status === 90 || game.game_status === 95)
  ) {
    return false;
  }
  return !isCurrentTimeMoreThanTenMinutesBefore(game.game_date_time);
}

export function isWithin48Hours(gameDateTime?: string): boolean {
  if (!gameDateTime) return false;
  try {
    const userTz = getUserTimezone();
    const parsed = parseGameDateTime(gameDateTime);
    if (!parsed.isValid()) return false;
    const twoDaysFromNow = dayjs().tz(userTz).add(2, "day");
    return parsed.tz(userTz).isBefore(twoDaysFromNow);
  } catch {
    return false;
  }
}

export function checkHasShowName(cardImageUrl?: string): boolean {
  if (!cardImageUrl) return true;
  try {
    const url = new URL(cardImageUrl);
    const showNames = url.searchParams.get("showNames");
    return showNames === "true";
  } catch {
    return true;
  }
}

